import {
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
	forwardRef,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	ArrowLeft,
	Check,
	Pencil,
	Plus,
	Search,
	Trash2,
	User,
} from 'lucide-react';
import { useGetPatient, useGetPatients, useRemovePatient } from '@/services';
import { decodeUrl, encodeUrl } from '@/utils';
import { cn } from '@/lib/utils';
import { PATIENT_GENDER_LABELS } from '@/constants/patient';
import {
	buildPatientDetailSections,
	DetailSectionBlock,
	formatCpfDisplay,
} from '@/utils/participantDetailDisplay';

import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import { Loader } from '@/components/ui';
import { PatientWhatsAppSchedulingNotice } from '@/components/whatsapp/PatientWhatsAppSchedulingNotice';
import { isPatientWhatsAppNotificationsActive } from '@/utils/patientWhatsAppNotifications';
import { CreatePatientModal } from './CreatePatientModal';

interface ProfessionalPatientSelectPanelProps {
	className?: string;
	/** Admin/Support: lista pacientes do profissional informado. */
	professionalId?: string;
}

export type ProfessionalPatientSelectPanelHandle = {
	openEditScrollToWhatsApp: () => void;
};

type PanelView = 'list' | 'detail';

const PATIENT_PANEL_BORDER_DRAW_MS = 1250;

const CHECK_BURST_RAYS = [
	{ angle: 0, length: 14 },
	{ angle: 30, length: 12 },
	{ angle: 60, length: 16 },
	{ angle: 90, length: 13 },
	{ angle: 120, length: 12 },
	{ angle: 150, length: 16 },
	{ angle: 180, length: 14 },
	{ angle: 210, length: 12 },
	{ angle: 240, length: 16 },
	{ angle: 270, length: 13 },
	{ angle: 300, length: 12 },
	{ angle: 330, length: 16 },
] as const;

const ProfessionalPatientSelectPanel = forwardRef<
	ProfessionalPatientSelectPanelHandle,
	ProfessionalPatientSelectPanelProps
>(({ className, professionalId }, ref) => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [searchTerm, setSearchTerm] = useState('');
	const [isCreatePatientModalOpen, setIsCreatePatientModalOpen] =
		useState(false);
	const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
	const [scrollEditToWhatsApp, setScrollEditToWhatsApp] = useState(false);
	const [isDeletePatientConfirmOpen, setIsDeletePatientConfirmOpen] =
		useState(false);

	const encodedParams = searchParams.get('d') || '';
	const decodedParams = decodeUrl<{
		patient_id?: string;
		s?: string;
		session_id?: string;
	}>(encodedParams);
	const selectedPatientId = decodedParams?.patient_id ?? '';
	const sessionIdFromParams = decodedParams?.session_id ?? '';
	const isEditMode = !!sessionIdFromParams;

	const [viewMode, setViewMode] = useState<PanelView>(() =>
		selectedPatientId ? 'detail' : 'list',
	);
	const [isBorderDrawComplete, setIsBorderDrawComplete] = useState(
		isEditMode && !!selectedPatientId,
	);
	const [playCheckPop, setPlayCheckPop] = useState(false);
	const borderFinishedRef = useRef(isEditMode && !!selectedPatientId);
	const listScrollRef = useRef<HTMLDivElement>(null);
	const detailScrollRef = useRef<HTMLDivElement>(null);
	const savedListScrollTopRef = useRef(0);

	useEffect(() => {
		if (!selectedPatientId && !isEditMode) setViewMode('list');
	}, [selectedPatientId, isEditMode]);

	useEffect(() => {
		if (!isEditMode || !selectedPatientId) return;

		borderFinishedRef.current = true;
		setIsBorderDrawComplete(true);
		setPlayCheckPop(false);
		setViewMode('detail');
	}, [isEditMode, selectedPatientId]);

	const finishBorderDraw = () => {
		if (borderFinishedRef.current) return;
		borderFinishedRef.current = true;
		setIsBorderDrawComplete(true);
		setPlayCheckPop(true);
	};

	useEffect(() => {
		if (viewMode !== 'detail' || isEditMode) return;

		const timer = window.setTimeout(
			finishBorderDraw,
			PATIENT_PANEL_BORDER_DRAW_MS,
		);

		return () => window.clearTimeout(timer);
	}, [viewMode, selectedPatientId, isEditMode]);

	const { data, isFetching } = useGetPatients({
		...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
		...(professionalId ? { professional_id: professionalId } : {}),
	});

	const { data: detailPatient } = useGetPatient(selectedPatientId, {
		enabled: viewMode === 'detail' && !!selectedPatientId,
	});

	const patients = data?.data ?? [];

	const selectedPatientFromList = useMemo(
		() => patients.find((patient) => patient.id === selectedPatientId),
		[patients, selectedPatientId],
	);

	const displayPatient = detailPatient ?? selectedPatientFromList;
	const showWhatsAppNotice =
		viewMode === 'detail' &&
		displayPatient != null &&
		isPatientWhatsAppNotificationsActive(displayPatient);

	const updatePatientInUrl = (patientId: string) => {
		const nextEncoded = encodeUrl({
			...(decodedParams || {}),
			patient_id: patientId,
			s: decodedParams?.s ?? '1',
		});
		searchParams.set('d', nextEncoded);
		navigate(`?${searchParams.toString()}`, { replace: true });
	};

	const clearPatientFromUrl = () => {
		const { patient_id: _removed, ...rest } = decodedParams || {};
		const nextEncoded = encodeUrl({
			...rest,
			s: decodedParams?.s ?? '1',
		});
		searchParams.set('d', nextEncoded);
		navigate(`?${searchParams.toString()}`, { replace: true });
	};

	useEffect(() => {
		if (viewMode !== 'detail') return;

		const detailScroll = detailScrollRef.current;
		if (!detailScroll) return;

		detailScroll.scrollTop = 0;
	}, [viewMode, selectedPatientId]);

	useEffect(() => {
		if (viewMode !== 'list') return;

		const listScroll = listScrollRef.current;
		if (!listScroll) return;

		listScroll.scrollTop = savedListScrollTopRef.current;
	}, [viewMode]);

	const handleSelectPatient = (patientId: string) => {
		if (listScrollRef.current) {
			savedListScrollTopRef.current = listScrollRef.current.scrollTop;
		}

		borderFinishedRef.current = false;
		setIsBorderDrawComplete(false);
		setPlayCheckPop(false);
		updatePatientInUrl(patientId);
		setViewMode('detail');
	};

	const handleBackToList = () => {
		clearPatientFromUrl();
		setViewMode('list');
		setIsBorderDrawComplete(false);
		setPlayCheckPop(false);
	};

	const { mutate: removePatient, isPending: isRemovingPatient } =
		useRemovePatient(() => {
			setIsDeletePatientConfirmOpen(false);
			handleBackToList();
		});

	const handleConfirmDeletePatient = () => {
		if (!selectedPatientId) return;
		removePatient(selectedPatientId);
	};

	const openEditPatientModal = (scrollToWhatsApp = false) => {
		setScrollEditToWhatsApp(scrollToWhatsApp);
		setIsEditPatientModalOpen(true);
	};
	const openDeletePatientConfirm = () => setIsDeletePatientConfirmOpen(true);

	useImperativeHandle(ref, () => ({
		openEditScrollToWhatsApp: () => openEditPatientModal(true),
	}));

	return (
		<aside
			className={cn(
				'relative isolate flex h-full min-h-0 w-full shrink-0 flex-col rounded-2xl bg-white shadow-sm',
				viewMode === 'list'
					? 'overflow-hidden border-2 border-zinc-100'
					: 'overflow-visible border-0',
				className,
			)}
		>
			{viewMode === 'detail' && (
				<div
					key={selectedPatientId}
					aria-hidden
					className={cn(
						'patient-panel-border-ring rounded-2xl',
						isBorderDrawComplete &&
							'patient-panel-border-ring--complete',
					)}
					onAnimationEnd={(event) => {
						if (
							event.animationName !== 'patient-panel-border-sweep'
						)
							return;
						finishBorderDraw();
					}}
				/>
			)}
			{viewMode === 'list' ? (
				<>
					<div className="relative z-0 shrink-0">
						<div className="px-4 py-4">
							<div className="flex items-start justify-between gap-2">
								<div className="min-w-0">
									<h2 className="text-sm font-bold text-zinc-800">
										Paciente
									</h2>
									<p className="mt-0.5 text-xs text-zinc-500">
										Selecione quem será atendido
									</p>
								</div>
								<button
									type="button"
									aria-label="Criar novo paciente"
									onClick={() =>
										setIsCreatePatientModalOpen(true)
									}
									className="flex size-9 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white shadow-sm transition-colors hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/40"
								>
									<Plus
										className="size-5"
										strokeWidth={2.5}
									/>
								</button>
							</div>
							<div className="relative mt-3">
								<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" />
								<input
									type="search"
									value={searchTerm}
									onChange={(event) =>
										setSearchTerm(event.target.value)
									}
									placeholder="Buscar por nome ou CPF"
									className="w-full rounded-xl border border-zinc-200 py-2.5 pl-9 pr-3 text-sm text-zinc-800 outline-none focus:border-brand-purple"
								/>
							</div>
						</div>
						<div aria-hidden className="border-b border-zinc-100" />
					</div>

					<div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-2xl">
						<div
							ref={listScrollRef}
							className="min-h-0 flex-1 overflow-y-auto p-3 max-lg:overscroll-y-auto xl:overscroll-contain"
						>
							<div className="flex flex-col gap-2">
								{isFetching ? (
									<div className="flex flex-1 items-center justify-center py-10">
										<Loader />
									</div>
								) : patients.length === 0 ? (
									<p className="py-8 text-center text-sm text-zinc-500">
										Nenhum paciente encontrado
									</p>
								) : (
									patients.map((patient) => {
										const cpfLabel = formatCpfDisplay(
											patient.cpf,
										);
										const genderLabel = patient.gender
											? PATIENT_GENDER_LABELS[
													patient.gender
											  ]
											: null;

										return (
											<div
												key={patient.id}
												role="button"
												tabIndex={0}
												onClick={() =>
													handleSelectPatient(
														patient.id,
													)
												}
												onKeyDown={(event) => {
													if (
														event.key === 'Enter' ||
														event.key === ' '
													) {
														event.preventDefault();
														handleSelectPatient(
															patient.id,
														);
													}
												}}
												className="flex w-full cursor-pointer items-start gap-3 rounded-xl border border-zinc-100 bg-zinc-50/50 p-3 text-left transition-colors hover:border-zinc-200 hover:bg-white"
											>
												<span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-600">
													<User className="size-5" />
												</span>
												<span className="min-w-0 flex-1">
													<span className="block truncate text-sm font-semibold text-zinc-800">
														{patient.full_name}
													</span>
													{cpfLabel && (
														<span className="mt-0.5 block text-xs text-zinc-500">
															CPF: {cpfLabel}
														</span>
													)}
													{genderLabel && (
														<span className="mt-0.5 block text-xs text-zinc-400">
															{genderLabel}
														</span>
													)}
												</span>
											</div>
										);
									})
								)}
							</div>
						</div>
					</div>
				</>
			) : (
				<>
					<div
						className={cn(
							'relative shrink-0 overflow-visible',
							playCheckPop && 'z-40',
						)}
					>
						<div className="flex items-center gap-3 px-4 py-3">
							{!isEditMode && (
								<button
									type="button"
									aria-label="Voltar para selecionar outro paciente"
									onClick={handleBackToList}
									className="flex size-8 shrink-0 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/40"
								>
									<ArrowLeft
										className="size-4"
										strokeWidth={2.5}
									/>
								</button>
							)}
							<div className="min-w-0 flex-1">
								<p className="text-[11px] font-bold uppercase tracking-wide text-emerald-600">
									{isEditMode
										? 'Paciente da consulta'
										: 'Paciente já selecionado'}
								</p>
								<p className="truncate text-sm font-semibold text-zinc-900">
									{displayPatient?.full_name ??
										'Carregando paciente...'}
								</p>
							</div>
							<span
								className={cn(
									'relative flex size-5 shrink-0 items-center justify-center overflow-visible',
									playCheckPop && 'z-40',
								)}
							>
								{playCheckPop && (
									<span
										aria-hidden
										className="patient-check-burst"
									>
										{CHECK_BURST_RAYS.map((ray) => (
											<span
												key={ray.angle}
												className="patient-check-burst-ray"
												style={
													{
														['--ray-angle' as string]: `${ray.angle}deg`,
														['--ray-length' as string]: `${ray.length}px`,
													} as React.CSSProperties
												}
											/>
										))}
									</span>
								)}
								<Check
									aria-hidden
									className={cn(
										'size-5 text-emerald-600',
										playCheckPop && 'patient-check-pop',
									)}
									strokeWidth={2.5}
									onAnimationEnd={(event) => {
										if (
											event.animationName !==
											'patient-check-pop'
										)
											return;
										setPlayCheckPop(false);
									}}
								/>
							</span>
						</div>
						<div aria-hidden className="border-b border-zinc-100" />
					</div>

					<div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-2xl">
						<div
							ref={detailScrollRef}
							className="min-h-0 flex-1 overflow-y-auto px-4 py-3 max-lg:overscroll-y-auto xl:overscroll-contain"
						>
							{!displayPatient ? (
								<div className="flex items-center justify-center py-12">
									<Loader />
								</div>
							) : (
								<div className="flex flex-col gap-3 pb-2">
									{showWhatsAppNotice ? (
										<PatientWhatsAppSchedulingNotice
											onAlter={() =>
												openEditPatientModal(true)
											}
										/>
									) : null}
									{buildPatientDetailSections(
										displayPatient,
										'scheduling',
									).map((section) => (
										<DetailSectionBlock
											key={section.title}
											{...section}
										/>
									))}
								</div>
							)}
						</div>

						<footer className="relative shrink-0 rounded-b-2xl border-t border-zinc-100 bg-white px-4 py-2.5">
							<div className="flex items-center gap-2">
								<button
									type="button"
									onClick={() => openEditPatientModal(false)}
									disabled={!displayPatient}
									className="inline-flex min-h-0 flex-1 items-center justify-center gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50/80 px-3 py-2 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50"
								>
									<Pencil
										className="size-4 shrink-0"
										strokeWidth={2.25}
									/>
									Editar
								</button>
								{!isEditMode && (
									<button
										type="button"
										onClick={openDeletePatientConfirm}
										disabled={
											!displayPatient || isRemovingPatient
										}
										className="inline-flex min-h-0 flex-1 items-center justify-center gap-1.5 rounded-lg border border-rose-100 bg-rose-50/80 px-3 py-2 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
									>
										<Trash2
											className="size-4 shrink-0"
											strokeWidth={2.25}
										/>
										Excluir
									</button>
								)}
							</div>
						</footer>
					</div>
				</>
			)}

			<CreatePatientModal
				isOpen={isCreatePatientModalOpen}
				onClose={() => setIsCreatePatientModalOpen(false)}
				onSuccess={(patient) => {
					handleSelectPatient(patient.id);
					setIsCreatePatientModalOpen(false);
				}}
				professionalId={professionalId}
				canEditWhatsAppNotifications
			/>

			<CreatePatientModal
				isOpen={isEditPatientModalOpen}
				patient={displayPatient ?? null}
				onClose={() => {
					setIsEditPatientModalOpen(false);
					setScrollEditToWhatsApp(false);
				}}
				onSuccess={() => {
					setIsEditPatientModalOpen(false);
					setScrollEditToWhatsApp(false);
				}}
				professionalId={professionalId}
				canEditWhatsAppNotifications
				scrollToWhatsAppSection={scrollEditToWhatsApp}
			/>

			<ConfirmActionModal
				isOpen={isDeletePatientConfirmOpen}
				onClose={() => setIsDeletePatientConfirmOpen(false)}
				onConfirm={handleConfirmDeletePatient}
				title="Excluir paciente?"
				description={
					displayPatient?.full_name
						? `Tem certeza que deseja excluir ${displayPatient.full_name}? Esta ação não pode ser desfeita.`
						: 'Tem certeza que deseja excluir este paciente? Esta ação não pode ser desfeita.'
				}
				confirmButtonText={
					isRemovingPatient ? 'Excluindo...' : 'Excluir paciente'
				}
				disableConfirm={isRemovingPatient}
			/>
		</aside>
	);
});

ProfessionalPatientSelectPanel.displayName = 'ProfessionalPatientSelectPanel';

export { ProfessionalPatientSelectPanel };
